<template>
    <div class="container-payment-methods">
        <div class="container-payment-methods-header">
            <div class="title">Categorias e Subcategorias de Pagamento</div>
        </div>

        <div class="container-payment-methods-categories">
            <div class="input-radio">
                <ui-radio
                    :checked="isCategory"
                    name="category"
                    value="category"
                    label="Categoria"
                    @input="radioHandler"
                />

                <ui-radio
                    :checked="!isCategory"
                    name="subcategory"
                    value="subcategory"
                    label="Subcategoria"
                    @input="radioHandler"
                />
            </div>

            <div class="categories-input-button">
                <ui-input
                    v-if="isCategory"
                    v-model="form.data.category_name"
                    :required="form.fields.category_name.required"
                    :disabled="form.fields.category_name.disabled"
                    label="Categoria:"
                    placeholder="Digite"
                    type="text"
                />

                <div v-else class="subcategories-input">
                    <ui-select2
                        v-model="form.data.category_id"
                        :data="categoryOptions"
                        :required="form.fields.category_id.required"
                        :disabled="form.fields.category_id.disabled"
                        design="mgb-15 br-all"
                        return-value="id"
                        item-label="name"
                        label="Categoria:"
                        placeholder="Selecione uma categoria"
                    />

                    <ui-input
                        v-model="form.data.subcategory_name"
                        :required="form.fields.subcategory_name.required"
                        :disabled="form.fields.subcategory_name.disabled"
                        label="Subcategoria:"
                        placeholder="Separe com vírgula para múltiplos"
                        type="text"
                    />
                </div>

                <div class="button">
                    <ui-button
                        :disabled="!canAdd"
                        :loading="loader.add"
                        design="md primary text-button"
                        @click="add">
                        Adicionar
                    </ui-button>
                </div>
            </div>

            <div v-if="!isCategory" class="subcategories-observations">
                <ui-switch
                    v-model="form.data.observations"
                    :checked="form.data.observations"
                    :required="form.fields.observations.required"
                    :disabled="form.fields.observations.disabled"
                    design="xs"
                    label="Observação obrigatória"
                />
            </div>
        </div>

        <div class="container-payment-methods-table">
            <div class="payment-methods-table">
                <ui-category-table v-if="isCategory" :contents="dataCategory" />
                <ui-subcategories-table v-else :contents="dataSubcategory" />
            </div>
        </div>

        <div class="container-payment-methods-footer">
            <div class="category-subcategory">
                <span class="categories">{{ dataCategory.length }} Categorias</span>
                <div class="line-horizontal" />
                <span class="categories">{{ dataSubcategory.length }} Subcategorias</span>
            </div>

            <ui-engine
                :next="{ name: 'launch.management.create.paymentPlans', index: 3 }"
                :disabled="!canAdvance"
                name-button="Avançar"
            />
        </div>
    </div>
</template>

<script>
	import {
		uiInput,
		uiRadio,
		uiSelect2,
		uiSwitch
	} from '@/components/ui/form'
	import { uiButton } from '@/components/ui/buttons'
	import uiCategoryTable from './table-payment-methods/category-table.vue'
	import uiSubcategoriesTable from './table-payment-methods/subcategories-table.vue'
	import uiEngine from '../engine'

	import { launchDay } from '@/services/axios'
	import { utilMixin } from '@/mixins'

	import { mapActions, mapGetters } from 'vuex'

	const defaultFormData = () => ({
		category_id: null,
		category_name: '',
		subcategory_name: '',
		observations: false
	})

	const defaultLoader = () => ({
		add: false
	})

	export default {
		name: 'ui-launch-payment-methods',

		components: {
			uiInput,
			uiSelect2,
			uiButton,
			uiCategoryTable,
			uiSubcategoriesTable,
			uiRadio,
			uiSwitch,
			uiEngine
		},

		mixins: [utilMixin],

		data() {
			return {
				form: {
					data: { ...defaultFormData() },
					fields: {
						category_id: {
							required: false,
							disabled: true,
						},
						category_name: {
							required: true,
							disabled: false,
						},
						subcategory_name: {
							required: false,
							disabled: true,
						},
						observations: {
							required: false,
							disabled: false,
						},
					}
				},
				next: { name: 'launch.management.create.paymentPlans', index: 3 },
				dataCategory: [],
				dataSubcategory: [],
				categoryOptions: [],
				isCategory: true,
				loader: { ...defaultLoader() }
			}
		},

		computed: {
			...mapGetters('launchDay', { config: 'getConfig' }),
			/**
			 * Computed property que define se é permitido adicionar uma categoria/subcategoria
			 */
			canAdd() {
				return this.validateForm().valid
			},
			/**
			 * Computed property que habilita ou desabilita o botão de avançar no cadastro, de
			 * acordo com as categorias e subcategorias
			 */
			canAdvance() {
				const subcategoriesCatIds = Object.values(this.dataSubcategory).map(subcategory => subcategory.category_id)
				const allCategoriesHasSub = Object.values(this.dataCategory).every(category => subcategoriesCatIds.includes(category.id))

				return this.dataCategory.length &&
					this.dataSubcategory.length &&
					allCategoriesHasSub
			}
		},

		created() {
			this.$root.$on('modal-msg-launch-payment-method-category-delete-response', () => {
				this.loadData('categories')
				this.loadData('subcategories')
			})
			this.$root.$on('modal-msg-launch-payment-method-subcategory-delete-response', () => {
				this.loadData('subcategories')
			})
		},

		mounted: async function () {
			if (!this.config?.id) {
				this.$router.push({ name: 'launch.management.create.mainData' })
				return
			}

			await this.loadData('categories')
			await this.loadData('subcategories')
		},

		beforeDestroy() {
			this.$root.$off('modal-msg-launch-payment-method-category-delete-response')
			this.$root.$off('modal-msg-launch-payment-method-subcategory-delete-response')
		},

		methods: {
			...mapActions('launchDay', ['setNextLoad']),
			/**
			 * Carrega os dados das categorias ou subcategorias
			 */
			loadData(url) {
				this.setNextLoad(true)
				return launchDay.getAll(url, {})
					.then(response => {

						if (url === 'categories') {
							this.dataCategory = [...response.data].filter(val => {
								return val.launchday_id === this.config.id
							})
							this.categoryOptions = [...response.data].filter(val => {
								return val.launchday_id === this.config.id
							})

						} else {
							this.dataSubcategory = [...response.data].filter(val => {
								return val.launchday_id === this.config.id
							})
						}
					})
					.catch(error => {
						if (error.data && error.data.msg) this.$toastr.e(error.data.msg)
						console.log(error)
					})
					.finally(() => {
						this.setNextLoad(false)
					})
			},
			/**
			 * Handler function que trata o radio button que define o tipo de cadastro
			 */
			radioHandler(value) {
				this.isCategory = value === 'category'
				if (this.isCategory) {
					this.form.data.category_id = null
					this.form.data.subcategory_name = ''
					this.form.data.observations = false

				} else {
					this.form.data.category_name = ''
				}
				this.form.fields.category_name.required = this.isCategory
				this.form.fields.category_name.disabled = !this.isCategory
				this.form.fields.category_id.required = !this.isCategory
				this.form.fields.category_id.disabled = this.isCategory
				this.form.fields.subcategory_name.required = !this.isCategory
				this.form.fields.subcategory_name.disabled = this.isCategory
			},
            /**
             * Valida a propriedade required dos campos do formulário
             */
            validateRequired(field) {
                const required = this.form.fields[field].required
                const validation = this.$mx_hasValue(this.form.data[field]) || parseInt(this.form.data[field]) === 0

                return (required && validation) || !required
            },
            /**
             * Valida os campos do formulário de acordo com diferentes conjuntos de regras
             */
            validateForm() {
                let valid = true
                let message = '<ul>'

                if (!this.validateRequired('category_id')) {
                    valid = false
                    message += '<li>É necessário selecionar uma categoria.</li>'
                }

                if (!this.validateRequired('category_name')) {
                    valid = false
                    message += '<li>É necessário informar um nome para a categoria.</li>'
                }

                if (!this.validateRequired('subcategory_name')) {
                    valid = false
                    message += '<li>É necessário informar um nome para a subcategoria.</li>'
                }

                message += '</ul>'

                return { valid, message }
            },
			/**
			 * Adiciona uma categoria ou subcategoria
			 */
			add() {
				if (!this.config) {
					this.$toastr.e('Nenhuma configuração de lançamento foi selecionada!')
					return
				}

				this.loader.add = true
				let url = 'categories'
				let params = {
					launchday_id: this.config.id,
					name: this.form.data.category_name
				}
				if (!this.isCategory) {
					url = 'subcategories'
					params = {
						category_id: this.form.data.category_id,
						launchday_id: this.config.id,
						name: this.form.data.subcategory_name,
						attributes: {
							observations: this.form.data.observations
						}
					}
				}

				launchDay.create(url, params)
					.then(response => {
						this.$toastr.s(response.data.msg)
						this.loadData(url)
						this.form.data = { ...defaultFormData() }
					})
					.catch(error => {
						if (error.data && error.data.msg) this.$toastr.e(error.data.msg)
						console.log(error)
					})
					.finally(() => {
						this.loader.add = false
					})
			}
		}
	}
</script>

<style lang="stylus" scoped>
	@import './styles.styl'
</style>